<template>
    <div class="white-rounded-box mb-7">
        <t-modal
            v-model="inviteModalShowing"
            @closed="inviteModalClosed"
        >
            <span>
                <h2>Invite new pros to join your organization</h2>
            </span>
            <span>
                <form @submit.prevent="fetchPros" class="flex items-center my-5">
                    <div class="relative w-full">
                        <div
                            class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
                        >
                            <svg
                                aria-hidden="true"
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                        <input
                            type="text"
                            v-model="search_text"
                            class="border border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2.5 dark:border-gray-400 dark:placeholder-gray-400 dark:text-black dark:focus:border-gray-300"
                            placeholder="Search for Pros"
                            required
                        />
                    </div>
                    <button
                        v-if="!isLoading"
                        type="submit"
                        :disabled="disabled"
                        class="p-2.5 mr-2 text-sm font-medium text-salesium-blue bg-white"
                    >
                        <svg
                            class="w-7 h-7"
                            fill="none"
                            :stroke="disabled ? '#777' : 'currentColor'"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            ></path>
                        </svg>
                        <span class="sr-only">Search</span>
                    </button>
                    <span
                        v-else
                        class="p-2.5 mr-2 text-xs font-medium text-salesium-blue bg-white"
                    >
                        <svg
                            class="animate-spin w-7 h-7"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-width="4"
                                stroke-dasharray="30 15"
                            ></circle>
                        </svg>
                        <span class="sr-only">Loading...</span>
                    </span>
                </form>
                <t-alert variant="danger" :show="showSearchError" @hidden="showSearchError = false">
                    {{searchErrorMessage}}
                </t-alert>
            </span>
            <span v-if="returnedPros && returnedPros.length">
                <div v-for="pro in returnedPros" :key="pro.id">
                    <div class="border-b mt-4">
                        <div class="ml-4 mr-4 mb-2 flex">
                            <div>
                                <div class="xl:mb-0 flex items-start">
                                    <button
                                        type="button"
                                        title="View Pro info"
                                        @click.prevent="sendInvite(pro)"
                                        class="flex"
                                    >
                                        <img
                                            v-show="
                                                notShowingDefaultAvatar.includes(
                                                    pro.id
                                                )
                                            "
                                            @load="
                                                notShowingDefaultAvatar.push(
                                                    pro.id
                                                )
                                            "
                                            :src="pro.avatar_uri"
                                            class="w-8 h-8 z-0 mr-2 rounded-full outline:none default-avatar-icon"
                                            :class="
                                                pro.care_score > 3
                                                    ? pro.care_score >= 4
                                                        ? 'badgeBlue'
                                                        : 'badgeGreen'
                                                    : ''
                                            "
                                        />
                                        <svg-vue
                                            v-show="
                                                !notShowingDefaultAvatar.includes(
                                                    pro.id
                                                )
                                            "
                                            icon="circle-user"
                                            title="default user avatar"
                                            class="h-6 mr-2 hover:cursor-pointer rounded-full bg-white"
                                            :class="
                                                pro.type == 'pro'
                                                    ? pro.care_score > 3
                                                        ? pro.care_score >= 4
                                                            ? 'fill-salesium-blue'
                                                            : 'fill-salesium-green'
                                                        : ''
                                                    : ''
                                            "
                                        />
                                        <div
                                            class="md:text-mini-lg text-hint font-bold"
                                        >
                                            {{ pro.display_name }}
                                        </div>
                                    </button>
                                </div>

                                <div class="mt-1 ml-2" v-if="pro.industry">
                                    <div class="flex items-center">
                                        <div class="mr-3 h-4 w-4 inline-block">
                                            <svg-vue
                                                :icon="
                                                    industryIcons[
                                                        pro.industry.name
                                                    ].icon
                                                "
                                                class="fill-black h-4 w-4"
                                            />
                                        </div>
                                        <p class="text-hint">
                                            {{ subindustryString(pro) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="prosInvitedSuccess.includes(pro.id)">
                        <t-alert variant="success" :show="true" @hidden="removeItemFromArray(prosInvitedSuccess, pro.id)">
                            Invite sent successfully!
                        </t-alert>
                    </div>
                    <div v-if="prosInvitedError.includes(pro.id)">
                        <t-alert variant="danger" :show="true" @hidden="removeItemFromArray(prosInvitedError, pro.id)">
                            Failed: Pro may already be invited or a member.
                        </t-alert>
                    </div>
                </div>
            </span>
            <span v-else-if="returnedPros && !returnedPros.length">
                <p>No search results found</p>
            </span>
        </t-modal>
        <div class="mx-7 mt-4">
            <div class="flex justify-center align-center mb-5">
                <h3 class="text-hint text-xl grow text-center">
                    Member Requests
                </h3>
                <button
                    class="tooltip"
                    type="button"
                    @click.prevent="toggleInviteModal"
                    title="Invite members"
                >
                    <div class="flex items-center hover:cursor-pointer">
                        <svg-vue
                            icon="plus-solid"
                            title="Invite contacts"
                            class="h-6 hover:cursor-pointer self-center"
                        />
                    </div>
                </button>
            </div>
            <div v-if="requests.length">
                <div
                    class="flex items-center mt-3"
                    v-for="(request, i) in requests"
                    :key="i"
                >
                    <request-card
                        :newMember="request"
                        :organization="organization"
                    />
                </div>
            </div>
            <div
                class="flex justify-between items-center text-center my-auto h-20"
                v-else
            >
                <p class="mx-auto">No Pending Members</p>
            </div>
        </div>
    </div>
</template>

<script>
import RequestCard from "./RequestCard.vue";
import { industryIcons } from "../../../constants/industryIcons";
export default {
    props: ["requests", "organization", "user"],
    components: { RequestCard },
    data() {
        return {
            industryIcons,
            inviteModalShowing: false,
            search_text: "",
            isLoading: false,
            returnedPros: null,
            notShowingDefaultAvatar: [],
            showSearchError: false,
            searchErrorMessage: "",
            prosInvitedSuccess: [],
            prosInvitedError: [],
            isAlreadyInvited: [],
        };
    },
    methods: {
        toggleInviteModal() {
            this.inviteModalShowing = !this.inviteModalShowing;
        },
        async fetchPros() {
            this.isLoading = true;
            await axios
                .post("/api/user/search", {
                    search_text: this.search_text,
                    industry_id: this.selectedIndustry,
                    search_location: (this.user.home_location.lat && this.user.home_location.lng) ? {
                        'lat': this.user.home_location.lat,
                        'lng': this.user.home_location.lng
                    } : ((this.user.business_location.lat && this.user.business_location.lng) ? {
                        'lat': this.user.business_location.lat,
                        'lng': this.user.business_location.lng
                    } : null),
                    relevance_threshold: 0.8,
                })
                .then((response) => {
                    this.isLoading = false;
//                    this.removeCurrentMembers(response.data);
                    console.log( JSON.stringify( response.data));
                    this.returnedPros = response.data;
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.searchErrorMessage = "Search failed!";
                    this.showSearchError = true;
                    console.log({ error });
                });
        },
//        removeCurrentMembers(allPros){
//            let result = [];
//            for(const pro of allPros){
//                if(!this.organization.users.some(member => member.id == pro.id)){
//                    result.push(pro);
//                }
//            }
//            this.returnedPros = result;
//        },
        subindustryString(pro) {
            if (pro.subindustry != null) {
                return pro.subindustry.tag.replaceAll("_", " ").substring(1);
            } else {
                return " ";
            }
        },
        sendInvite(pro){
            if(this.isAlreadyInvited.includes(pro.id)){
                alert("pro already invited");
                return;
            }
            axios
                .post(`/api/organizations/${this.organization.id}/invite`, {
                    pro_id: pro.id,
                })
                .then((response) => {
                    this.isLoading = false;
                    this.isAlreadyInvited.push(pro.id);
                    this.prosInvitedSuccess.push(pro.id);
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.prosInvitedError.push(pro.id);
                    console.log({ error });
                });
        },
        removeItemFromArray(arr, item){
            let index = arr.indexOf(item);
            if(index){
                arr.splice(index, 1);
            }
        },
        inviteModalClosed(){
            this.search_text = "";
            this.inviteModalShowing = false;
            this.prosInvitedSuccess = [];
            this.prosInvitedError = [];
            this.returnedPros = null;
        }
    },
    computed: {
        disabled() {
            return this.search_text.trim().length === 0;
        },
    },
};
</script>
